import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  CircularProgress,
  Paper,
} from '@mui/material'
import { MonetizationOn } from '@mui/icons-material'
import { formatDate, formatCurrency } from 'src/utils/Formatter'
import { getPatientInvoiceDetails } from 'src/services/LookupService'
import { PatientInvoiceDetailSummary } from 'src/viewModels/workflow/PatientInvoiceDetailSummary'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import { CaseSummaryResult } from 'src/obmCaseSearch/CaseSearchRequest'

interface IPatientInvoiceDetailsModalProps {
  open: boolean
  detail?: any
  caseSummary?: CaseSummaryResult
  onClose: () => void
}

const PatientInvoiceDetailsModal: React.FC<IPatientInvoiceDetailsModalProps> = ({
  open,
  detail,
  caseSummary,
  onClose,
}) => {
  const [invoiceDetails, setInvoiceDetails] = useState<PatientInvoiceDetailSummary[]>([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (open && detail) {
      setIsLoading(true)
      getPatientInvoiceDetails(detail)
        .then(setInvoiceDetails)
        .finally(() => setIsLoading(false))
    }
  }, [open, detail])

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={onClose}>
      <ResponsiveAppBar
        title={`Case Line Items${
          caseSummary?.patientName ? ` — ${caseSummary.patientName}` : ''
        }${caseSummary?.caseNumber ? ` (Case #: ${caseSummary.caseNumber})` : ''}`}
        pageIcon={<MonetizationOn />}
        isModal={true}
      />
      <DialogContent>
        {isLoading && (
          <div
            style={{
              position: 'fixed',
              display: 'flex',
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: '1200',
            }}
          >
            <CircularProgress size={100} />
          </div>
        )}
        <Paper>
          <Table>
            <TableHead>
              <TableRow style={{ fontWeight: 'bold' }}>
                <TableCell align="right">Group #</TableCell>
                <TableCell align="right">Invoice #</TableCell>
                <TableCell align="right">Invoice Date</TableCell>
                <TableCell align="right"># of Hours</TableCell>
                <TableCell align="right">Price Per Hour</TableCell>
                <TableCell align="right">Invoice Amount</TableCell>
                <TableCell align="right">Amount Paid</TableCell>
                <TableCell>Invoice Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoiceDetails.map((row, idx) => (
                <TableRow key={idx}>
                  <TableCell align="right">{row.groupNumber ?? ''}</TableCell>
                  <TableCell align="right">{row.invoiceNumber ?? ''}</TableCell>
                  <TableCell align="right">
                    {row.invoiceDate ? formatDate(String(row.invoiceDate)) : ''}
                  </TableCell>
                  <TableCell align="right">{row.numberOfHours ?? ''}</TableCell>
                  <TableCell align="right">
                    {row.billingPricePerHour != null &&
                    !isNaN(Number(row.billingPricePerHour))
                      ? formatCurrency(String(row.billingPricePerHour))
                      : ''}
                  </TableCell>
                  <TableCell align="right">
                    {row.invoiceAmount != null && !isNaN(Number(row.invoiceAmount))
                      ? formatCurrency(String(row.invoiceAmount))
                      : ''}
                  </TableCell>
                  <TableCell align="right">
                    {row.amountPaid != null && !isNaN(Number(row.amountPaid))
                      ? formatCurrency(String(row.amountPaid))
                      : ''}
                  </TableCell>
                  <TableCell>{row.invoiceNotes ?? ''}</TableCell>
                </TableRow>
              ))}
              {invoiceDetails.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography variant="body2" color="textSecondary" align="center">
                      No records found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default PatientInvoiceDetailsModal
